import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, Share2, Users, UserCheck, UserPlus, Search } from "lucide-react";
import { DashboardLayout, Panel, StatCard, StatusBadge } from "@/components/DashboardLayout";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const BookingsSummary = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/api/bookings").then((r) => setItems(r.data.items)).catch(() => {});
  }, []);

  const open = items.filter((b) => b.is_open).length;
  const awaiting = items.filter((b) => b.status === "AWAITING_CONFIRMATION").length;

  return (
    <Panel title="Care bookings" description="Request visits from verified providers and confirm delivery." testId="mc-bookings-panel">
      <div className="flex flex-wrap gap-6 text-sm">
        <div><p className="font-display text-2xl font-extrabold text-slate-900" data-testid="mc-bookings-total">{items.length}</p><p className="text-xs text-slate-500">Total</p></div>
        <div><p className="font-display text-2xl font-extrabold text-slate-900" data-testid="mc-bookings-open">{open}</p><p className="text-xs text-slate-500">In progress</p></div>
        <div><p className="font-display text-2xl font-extrabold text-orange-600" data-testid="mc-bookings-awaiting">{awaiting}</p><p className="text-xs text-slate-500">Awaiting confirmation</p></div>
      </div>
      <Button asChild variant="outline" className="mt-5 rounded-full">
        <Link to="/bookings" data-testid="mc-open-bookings-btn">Manage bookings</Link>
      </Button>
    </Panel>
  );
};

const NAV = [
  { to: "/dashboard/master-client", label: "Overview", testId: "nav-overview" },
  { to: "/beneficiaries", label: "Beneficiaries", testId: "nav-beneficiaries" },
  { to: "/witnesses", label: "Witnesses", testId: "nav-witnesses" },
  { to: "/marketplace", label: "Find providers", testId: "nav-marketplace" },
  { to: "/bookings", label: "Bookings", testId: "nav-bookings" },
];

export default function MasterClientDashboard() {
  const { user } = useAuth();
  const [kyc, setKyc] = useState(null);
  const [counts, setCounts] = useState({ beneficiaries: 0, witnesses: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [k, b, w] = await Promise.all([
          api.get("/api/kyc/me"),
          api.get("/api/beneficiaries").catch(() => ({ data: [] })),
          api.get("/api/witnesses").catch(() => ({ data: [] })),
        ]);
        setKyc(k.data);
        setCounts({ beneficiaries: b.data.length, witnesses: w.data.length });
      } catch (e) {
        toast.error(apiError(e));
      }
    })();
  }, []);

  const verified = user?.kyc_status === "VERIFIED";

  return (
    <DashboardLayout
      title={`Welcome, ${user?.full_name?.split(" ")[0]}`}
      subtitle="Client dashboard — manage verification, beneficiaries and witnesses."
      nav={NAV}
      badge={<StatusBadge status={user?.kyc_status} testId="mc-kyc-badge" />}
    >
      <div className="grid gap-6">
        {!verified && (
          <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-6" data-testid="kyc-gate-banner">
            <ShieldAlert className="h-6 w-6 text-amber-700" />
            <div className="min-w-0 flex-1">
              <p className="font-display font-bold text-amber-900">Verification required</p>
              <p className="mt-1 text-sm text-amber-800">
                {kyc?.record
                  ? `Your KYC status is ${kyc.status.replaceAll("_", " ").toLowerCase()}. Verified healthcare providers unlock once an administrator approves it.`
                  : "Complete your KYC to unlock verified healthcare providers, beneficiaries and witnesses."}
              </p>
              {kyc?.record?.reviewer_notes && (
                <p className="mt-2 text-sm font-medium text-amber-900" data-testid="kyc-reviewer-notes">
                  Reviewer note: {kyc.record.reviewer_notes}
                </p>
              )}
            </div>
            {(!kyc?.record || ["PENDING", "REJECTED", "CORRECTION_REQUESTED"].includes(kyc.status)) && (
              <Button asChild className="rounded-full bg-emerald-900 hover:bg-emerald-800">
                <Link to="/onboarding/master-client" data-testid="mc-start-kyc-btn">
                  {kyc?.record ? "Resubmit KYC" : "Start KYC"}
                </Link>
              </Button>
            )}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard label="KYC status" value={(user?.kyc_status || "PENDING").replaceAll("_", " ")} testId="stat-kyc" />
          <StatCard label="Beneficiaries" value={counts.beneficiaries} hint="Profiles you manage" testId="stat-beneficiaries" />
          <StatCard label="Witnesses" value={counts.witnesses} hint="Trusted contacts on record" testId="stat-witnesses" />
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {[
            { icon: Users, title: "Beneficiary profiles", body: "Add the people who will receive care, with medical condition and service address.", to: "/beneficiaries", cta: "Manage beneficiaries", testId: "card-beneficiaries" },
            { icon: UserCheck, title: "Witnesses", body: "Record trusted people who confirm care visits. No formal KYC needed.", to: "/witnesses", cta: "Manage witnesses", testId: "card-witnesses" },
            { icon: Search, title: "Find providers", body: "Browse verified doctors, nurses and therapists with fees and availability.", to: "/marketplace", cta: "Open marketplace", testId: "card-marketplace" },
          ].map((c) => (
            <Panel key={c.title} testId={c.testId}>
              <c.icon className="h-6 w-6 text-emerald-700" />
              <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{c.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{c.body}</p>
              <Button asChild variant="outline" className="mt-5 rounded-full">
                <Link to={c.to}>{c.cta}</Link>
              </Button>
            </Panel>
          ))}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <BookingsSummary />
          <Panel title="Notifications" description="Booking updates, KYC decisions and next-day visit reminders arrive in the bell above." testId="mc-notifications-panel">
            <p className="text-sm text-slate-600">
              CARENEST reminds you, your provider and your witness the morning before every scheduled visit.
            </p>
          </Panel>
        </div>
        <Panel title="Grow the CARENEST circle" description="Add a witness for your visits, or refer another family and earn recognition as a CARENEST referrer." testId="mc-referrer-witness-panel">
          <div className="flex flex-wrap gap-3">
            <Button asChild className="rounded-full bg-emerald-900 hover:bg-emerald-800">
              <Link to="/witnesses" data-testid="cta-add-witness-btn">
                <UserPlus className="mr-2 h-4 w-4" /> Add a witness
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full">
              <Link to="/register?role=REFERRER" data-testid="cta-become-referrer-btn">
                <Share2 className="mr-2 h-4 w-4" /> Become a referrer
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full">
              <a href="/witness-form.html" target="_blank" rel="noreferrer" data-testid="cta-witness-form-btn">
                Download witness form
              </a>
            </Button>
          </div>
          <p className="mt-4 text-xs text-slate-500">
            Witnesses do not need an account — they just sign the declaration form and provide an ID.
          </p>
        </Panel>
      </div>
    </DashboardLayout>
  );
}
